import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ChartData, ChartOptions } from 'chart.js';
import {
  DashboardWidget,
  StatMetric,
  VenueMapMarker,
} from '../../../../../models/big-screen-dashboard.model';
import { EntranceGroup } from '../../entrance-groups/entrance-groups.component';
import {
  EntranceRate,
  ErrorAggregate,
  applyEntranceGroups,
  computeEntranceRates,
  computeErrorAggregates,
  computeScanRateTotals,
  formatErrorCode,
} from '../../../../../services/attendance-metrics';
import { TimedEntry, buildChartOptions, buildTimedChartData } from '../../../../../services/attendance-chart';

/** Human labels for each stat-tile metric. */
const STAT_LABELS: Record<StatMetric, string> = {
  entries: 'Entries',
  inVenue: 'Fans Inside',
  exits: 'Exits',
  reentries: 'Reentries',
  internal: 'Internal',
  totalScanRate: 'Total Scan Rate',
  successRate: 'Successful Scans',
  errorRate: 'Error Scans',
};

/** Metrics expressed as a per-minute rate rather than a running count. */
const RATE_METRICS: ReadonlySet<StatMetric> = new Set<StatMetric>([
  'totalScanRate',
  'successRate',
  'errorRate',
]);

/**
 * Rate metrics shown as whole numbers. The successful and total scan rates run
 * into the hundreds/min on a busy gate, where a trailing decimal is just noise;
 * the error rate keeps its decimal since it's typically a small fraction.
 */
const INTEGER_RATE_METRICS: ReadonlySet<StatMetric> = new Set<StatMetric>([
  'totalScanRate',
  'successRate',
]);

/**
 * Renders a single dashboard widget's body from the live `attendanceSummary` and
 * timed chart data. Purely presentational: it derives everything from its inputs
 * via the shared attendance-metrics/attendance-chart helpers so its figures match
 * the modern view and the base graph exactly.
 */
@Component({
  selector: 'app-widget-content',
  standalone: false,
  templateUrl: './widget-content.component.html',
  styleUrls: ['./widget-content.component.scss'],
})
export class WidgetContentComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() widget!: DashboardWidget;
  @Input() attendanceSummary: any = {};
  @Input() entranceGroups: EntranceGroup[] = [];
  @Input() timedGateEntries: TimedEntry[] = [];
  @Input() timedScannerEntries: TimedEntry[] = [];
  @Input() chartColors: string[] = [];
  @Input() dark: boolean = false;
  /** True in the dashboard's edit mode; enables inline venue-map marker dragging. */
  @Input() editing: boolean = false;

  /** Emits the widget with updated fields (e.g. moved markers) for persistence. */
  @Output() widgetChange = new EventEmitter<DashboardWidget>();

  /** The stat tile's wrapper and number, for fitting the value to the box. */
  @ViewChild('statWrap') statWrap?: ElementRef<HTMLElement>;
  @ViewChild('statValueEl') statValueEl?: ElementRef<HTMLElement>;

  /** Derived, recomputed on any input change. */
  entranceTiles: EntranceRate[] = [];
  errorTiles: ErrorAggregate[] = [];
  statValue: number = 0;
  statLabel: string = '';
  statIsRate: boolean = false;
  /** Decimal places for the stat tile's number: 0 for count/integer-rate metrics. */
  statDecimals: number = 0;
  chartData: ChartData = { labels: [], datasets: [] };
  chartOptions: ChartOptions = buildChartOptions(false);

  /** Watches the stat tile's box so the number refits when the widget resizes. */
  private statResizeObserver?: ResizeObserver;

  constructor(private readonly zone: NgZone) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.recompute();
    // The value or metric may have changed; refit once the DOM updates.
    this.scheduleStatFit();
  }

  ngAfterViewInit(): void {
    this.observeStatBox();
    this.scheduleStatFit();
  }

  ngOnDestroy(): void {
    this.statResizeObserver?.disconnect();
  }

  /** True while the venue is actively scanning; gates rate-vs-count displays. */
  get hasScanRate(): boolean {
    return computeScanRateTotals(this.attendanceSummary).totalScanRate > 0;
  }

  /**
   * Inline style overrides for the widget's title/heading, from the user's
   * font-size/bold/colour choices. Each is applied only when set, so an unset
   * option keeps the theme's responsive default (returning null lets the
   * stylesheet win). Shared by the stat-tile label and every grid/graph heading.
   */
  get titleStyle(): { [key: string]: string | null } {
    return {
      'font-size': this.widget.titleFontSize ? `${this.widget.titleFontSize}px` : null,
      'font-weight': this.widget.titleBold ? '800' : null,
      color: this.widget.titleColor || null,
    };
  }

  /**
   * Heading for a single-entrance graph: the plotted line's label, or a fallback
   * while the chart has no data yet (a freshly-added widget has empty datasets, so
   * indexing [0] returns undefined — reading .label on it directly would throw and
   * break change detection for the whole dashboard).
   */
  get entranceGraphLabel(): string {
    const label = this.chartData.datasets[0]?.label;
    return typeof label === 'string' && label.length > 0 ? label : 'Entrance';
  }

  formatErrorCode(code: string): string {
    return formatErrorCode(code);
  }

  /** Persists moved venue-map markers by emitting the updated widget upward. */
  onMarkersChange(markers: VenueMapMarker[]): void {
    this.widgetChange.emit({ ...this.widget, markers });
  }

  /**
   * Observes the stat tile's wrapper so the headline number refits whenever the
   * widget is resized. Runs outside Angular — this only mutates an inline
   * font-size and must not trigger change detection on every resize frame.
   */
  private observeStatBox(): void {
    const wrap = this.statWrap?.nativeElement;
    if (!wrap || typeof ResizeObserver === 'undefined') return;
    this.zone.runOutsideAngular(() => {
      this.statResizeObserver = new ResizeObserver(() => this.fitStatValue());
      this.statResizeObserver.observe(wrap);
    });
  }

  /** Refits the number after the view has rendered the latest value. */
  private scheduleStatFit(): void {
    if (this.widget?.type !== 'statTile') return;
    // Defer to the next frame so the new digits/box size are laid out first.
    this.zone.runOutsideAngular(() => requestAnimationFrame(() => this.fitStatValue()));
  }

  /**
   * Sizes the stat number to fill its box on both axes. A single CSS
   * container-unit can only track one dimension, so a wide-but-short tile leaves
   * blank space; here the font-size is scaled by the tighter of the width and
   * height ratios (minus a small margin) so the number fills whichever dimension
   * constrains it, for any tile aspect ratio or digit count.
   */
  private fitStatValue(): void {
    const box = this.statValueEl?.nativeElement;
    if (!box) return;
    const availW = box.clientWidth;
    const availH = box.clientHeight;
    if (availW <= 0 || availH <= 0) return;

    // Measure the number at a known reference size, then scale by the tighter
    // axis ratio. scrollWidth/scrollHeight are clamped to the box's own size, so
    // a number smaller than the box measures as the box and never grows — union
    // the children's rects instead to get the true content extent.
    const reference = 100;
    box.style.fontSize = `${reference}px`;
    let contentW = 0;
    let contentH = 0;
    for (const child of Array.from(box.children) as HTMLElement[]) {
      const rect = child.getBoundingClientRect();
      contentW += rect.width;
      contentH = Math.max(contentH, rect.height);
    }
    if (contentW <= 0 || contentH <= 0) return;

    const margin = 0.9; // leave a little breathing room inside the tile
    const scale = Math.min(availW / contentW, availH / contentH) * margin;
    const fitted = Math.max(12, reference * scale);
    box.style.fontSize = `${fitted}px`;
  }

  private recompute(): void {
    switch (this.widget?.type) {
      case 'statTile':
        this.recomputeStat();
        break;
      case 'scanRateByEntrance':
        this.recomputeEntrances();
        break;
      case 'errorsByResultCode':
        this.recomputeErrors();
        break;
      case 'multiLineGraph':
      case 'entranceGraph':
        this.recomputeChart();
        break;
    }
  }

  private recomputeStat(): void {
    const metric: StatMetric = this.widget.statMetric ?? 'entries';
    this.statLabel = STAT_LABELS[metric];
    this.statIsRate = RATE_METRICS.has(metric);
    // Rates show one decimal by default; count and integer-rate metrics show none.
    this.statDecimals = this.statIsRate && !INTEGER_RATE_METRICS.has(metric) ? 1 : 0;
    const summary = this.attendanceSummary || {};
    const totals = computeScanRateTotals(summary);
    switch (metric) {
      case 'totalScanRate':
        this.statValue = totals.totalScanRate;
        break;
      case 'successRate':
        this.statValue = totals.totalSuccessRate;
        break;
      case 'errorRate':
        this.statValue = totals.totalErrorRate;
        break;
      default:
        this.statValue = Number(summary[metric] || 0);
    }
  }

  private recomputeEntrances(): void {
    const rates = computeEntranceRates(this.attendanceSummary);
    const grouped = applyEntranceGroups(
      rates,
      this.entranceGroups,
      !!this.widget.useGrouping && this.entranceGroups.length > 0
    );
    // An unset selection means "all"; an explicit list (even empty) is literal, so
    // unselecting everything in the config shows an empty grid.
    const selection = this.widget.entranceNames;
    this.entranceTiles =
      selection === undefined
        ? grouped
        : grouped.filter((tile) => selection.includes(tile.name));
  }

  private recomputeErrors(): void {
    const all = computeErrorAggregates(this.attendanceSummary);
    // Unset means "all"; an explicit (possibly empty) list is taken literally.
    const selection = this.widget.errorCodes;
    this.errorTiles =
      selection === undefined ? all : all.filter((err) => selection.includes(err.code));
  }

  private recomputeChart(): void {
    const source: TimedEntry[] =
      this.widget.graphSource === 'scanner' ? this.timedScannerEntries : this.timedGateEntries;
    // entranceGraph plots a single line; multiLineGraph plots the selection (or all
    // when unset — an explicit empty list plots no lines).
    const allNames = source.map((entry) => entry.gate);
    const selection = this.widget.entranceNames;
    let names = selection === undefined ? allNames : selection;
    if (this.widget.type === 'entranceGraph') {
      names = names.slice(0, 1);
    }
    this.chartData = buildTimedChartData(source, names, this.chartColors, this.widget.type === 'multiLineGraph');
    this.chartOptions = buildChartOptions(this.dark);
  }
}
